import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import GameCertificate from "@/components/GameCertificate";
import { 
  Play, 
  Users, 
  Trophy, 
  Timer, 
  ArrowRight,
  Loader2,
  SkipForward,
  CheckCircle2,
  Award,
  BarChart3,
  Sparkles,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function GameDemoPage({ user, onLogout }) {
  const navigate = useNavigate();
  
  // State
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [playerCount, setPlayerCount] = useState(5);
  const [demoState, setDemoState] = useState("setup"); // setup, playing, results
  const [demoId, setDemoId] = useState(null);
  const [gameCode, setGameCode] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [answerDistribution, setAnswerDistribution] = useState({});
  const [finalResults, setFinalResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [businessPlan, setBusinessPlan] = useState(null);
  const [generatingPlan, setGeneratingPlan] = useState(false);

  // Fetch modules
  useEffect(() => {
    fetchModules();
  }, []);

  // Auto-advance timer
  useEffect(() => {
    if (autoAdvance && demoState === "playing" && currentQuestion) {
      const timer = setTimeout(() => {
        handleNextQuestion();
      }, 3000); // 3 seconds per question in auto mode
      return () => clearTimeout(timer);
    }
  }, [autoAdvance, currentQuestion, demoState]);

  const fetchModules = async () => {
    try {
      const response = await axios.get(`${API}/game/modules`);
      setModules(response.data);
      if (response.data.length > 0) {
        setSelectedModule(response.data[0]);
      }
    } catch (error) {
      console.error("Failed to fetch modules:", error);
    }
  };

  const startDemo = async () => {
    if (!selectedModule) return;
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/game/demo/start`, {
        module_id: selectedModule.id,
        simulated_players: playerCount
      });
      
      setDemoId(response.data.demo_id);
      setTotalQuestions(response.data.total_questions);
      setLeaderboard(response.data.players.map((p, i) => ({
        rank: i + 1,
        nickname: p.nickname,
        points: 0
      })));
      setDemoState("playing");
      
      // Start first question
      await handleNextQuestion(response.data.demo_id);
    } catch (error) {
      console.error("Failed to start demo:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNextQuestion = async (id = demoId) => {
    if (!id) return;
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/game/demo/${id}/next`);
      
      if (response.data.error) {
        // No more questions
        await completeDemo(id);
        return;
      }
      
      setCurrentQuestion(response.data.question);
      setQuestionNumber(response.data.question_number);
      setLeaderboard(response.data.leaderboard);
      setAnswerDistribution(response.data.answer_distribution);
      
      if (response.data.is_last_question) {
        // Auto-complete after showing last question results
        setTimeout(() => completeDemo(id), autoAdvance ? 3000 : 0);
      }
    } catch (error) {
      console.error("Failed to get next question:", error);
    } finally {
      setLoading(false);
    }
  };

  const completeDemo = async (id = demoId) => {
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/game/demo/${id}/complete`);
      setFinalResults(response.data);
      setGameCode(response.data.game_code);
      setDemoState("results");
    } catch (error) {
      console.error("Failed to complete demo:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateCertificate = async () => {
    if (!gameCode || !finalResults?.winner) return;
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/game/generate-certificate`, {
        game_code: gameCode,
        player_id: finalResults.winner.player_id,
        business_name: `${finalResults.winner.nickname}'s Lemonade Stand`
      });
      
      if (response.data.success) {
        setCertificate(response.data.certificate);
        setShowCertificate(true);
      }
    } catch (error) {
      console.error("Failed to generate certificate:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateBusinessPlan = async () => {
    if (!gameCode || !finalResults?.winner) return;
    setGeneratingPlan(true);
    
    try {
      const response = await axios.post(`${API}/game/generate-business-plan`, {
        game_code: gameCode,
        player_id: finalResults.winner.player_id,
        business_name: `${finalResults.winner.nickname}'s Lemonade Stand`
      });
      
      if (response.data.success) {
        setBusinessPlan(response.data);
      }
    } catch (error) {
      console.error("Failed to generate business plan:", error);
    } finally {
      setGeneratingPlan(false);
    }
  };

  const resetDemo = () => {
    setDemoState("setup");
    setDemoId(null);
    setGameCode(null);
    setCurrentQuestion(null);
    setQuestionNumber(0);
    setLeaderboard([]);
    setAnswerDistribution({});
    setFinalResults(null);
    setCertificate(null);
    setBusinessPlan(null);
    setAutoAdvance(false);
  };

  const optionColors = {
    A: "bg-red-500",
    B: "bg-blue-500",
    C: "bg-green-500",
    D: "bg-yellow-500"
  };

  // Setup Screen
  const renderSetup = () => (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4">
          <Sparkles className="w-5 h-5" />
          <span className="font-semibold">Demo Mode</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Quick Demo for Presentations
        </h1>
        <p className="text-gray-600">
          One-click demo with simulated players. Perfect for sales calls and walkthroughs.
        </p>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-lg">
        {/* Module Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Game Module
          </label>
          <div className="grid grid-cols-3 gap-3">
            {modules.map((module) => (
              <button
                key={module.id}
                onClick={() => setSelectedModule(module)}
                className={`p-4 rounded-xl border-2 transition-all text-center ${
                  selectedModule?.id === module.id
                    ? "border-yellow-500 bg-yellow-50"
                    : "border-gray-200 hover:border-yellow-300"
                }`}
              >
                <div className="text-2xl mb-1">
                  {module.session_number === 1 && "💡"}
                  {module.session_number === 2 && "🦅"}
                  {module.session_number === 3 && "📋"}
                </div>
                <div className="font-semibold text-sm">{module.title}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Player Count */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Simulated Players: {playerCount}
          </label>
          <input
            type="range"
            min="3"
            max="10"
            value={playerCount}
            onChange={(e) => setPlayerCount(parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>3</span>
            <span>10</span>
          </div>
        </div>

        {/* Auto-Advance Toggle */}
        <div className="mb-8">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={autoAdvance}
              onChange={(e) => setAutoAdvance(e.target.checked)}
              className="w-5 h-5 rounded text-yellow-500 focus:ring-yellow-500"
            />
            <div>
              <span className="font-medium text-gray-900">Auto-Advance Mode</span>
              <p className="text-sm text-gray-500">
                Questions advance automatically (3 seconds each)
              </p>
            </div>
          </label>
        </div>

        {/* Start Button */}
        <button
          onClick={startDemo}
          disabled={!selectedModule || loading}
          className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-all disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Play className="w-5 h-5" />
              Start Demo
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Playing Screen
  const renderPlaying = () => (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-semibold">
            Demo Mode
          </span>
          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
            Q{questionNumber}/{totalQuestions}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {autoAdvance && (
            <span className="text-sm text-gray-500 flex items-center gap-1">
              <Timer className="w-4 h-4" />
              Auto-advancing...
            </span>
          )}
          <button
            onClick={resetDemo}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Question Card */}
      {currentQuestion && (
        <>
          <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-3xl p-8 mb-6 text-white">
            <div className="flex items-start gap-4">
              <div className="bg-white/20 p-3 rounded-xl">
                <BarChart3 className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold leading-tight">
                  {currentQuestion.question}
                </h2>
              </div>
            </div>
          </div>

          {/* Options with Distribution */}
          {currentQuestion.options && (
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {currentQuestion.options.map((option) => {
                const count = answerDistribution[option.id] || 0;
                const total = Object.values(answerDistribution).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                const isCorrect = currentQuestion.correct === option.id;
                
                return (
                  <div
                    key={option.id}
                    className={`${optionColors[option.id]} rounded-2xl p-6 text-white relative overflow-hidden`}
                  >
                    <div className="relative z-10">
                      <div className="text-4xl font-bold opacity-50 mb-2">{option.id}</div>
                      <div className="text-lg font-semibold">{option.text}</div>
                      <div className="mt-3 flex items-center gap-2">
                        <span className="text-2xl font-bold">{percentage}%</span>
                        <span className="text-sm opacity-75">({count} players)</span>
                        {currentQuestion.type === "quiz" && isCorrect && (
                          <CheckCircle2 className="w-6 h-6" />
                        )}
                      </div>
                    </div>
                    <div 
                      className="absolute bottom-0 left-0 h-1 bg-white/50"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Leaderboard */}
      <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          Live Leaderboard
        </h3>
        <div className="space-y-2">
          {leaderboard.slice(0, 5).map((entry, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between p-3 rounded-lg ${
                idx === 0 ? "bg-yellow-100 border-2 border-yellow-300" : "bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  idx === 0 ? "bg-yellow-500 text-white" :
                  idx === 1 ? "bg-gray-400 text-white" :
                  idx === 2 ? "bg-amber-600 text-white" :
                  "bg-gray-200 text-gray-600"
                }`}>
                  {entry.rank}
                </span>
                <span className="font-semibold">{entry.nickname}</span>
              </div>
              <span className="font-bold text-yellow-600">{entry.points} pts</span>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      {!autoAdvance && (
        <div className="text-center">
          <button
            onClick={() => handleNextQuestion()}
            disabled={loading}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-3 rounded-full font-bold text-lg inline-flex items-center gap-2 transition-all disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <SkipForward className="w-5 h-5" />
                Next Question
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  // Results Screen
  const renderResults = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">🎉</div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Demo Complete!</h1>
        <p className="text-gray-600">
          This is what participants see at the end of a live game session.
        </p>
      </div>

      {/* Winner Spotlight */}
      {finalResults?.winner && (
        <div className="bg-gradient-to-br from-yellow-400 to-orange-500 rounded-3xl p-8 text-white text-center mb-8">
          <div className="text-5xl mb-4">🏆</div>
          <h2 className="text-2xl font-bold mb-2">Winner!</h2>
          <div className="text-4xl font-bold mb-2">{finalResults.winner.nickname}</div>
          <div className="text-xl opacity-90">{finalResults.winner.points} points</div>
        </div>
      )}

      {/* Full Leaderboard */}
      <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
        <h3 className="font-bold text-xl text-gray-900 mb-6 flex items-center gap-2">
          <Trophy className="w-6 h-6 text-yellow-500" />
          Final Standings
        </h3>
        <div className="space-y-3">
          {finalResults?.final_leaderboard?.map((entry, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between p-4 rounded-xl ${
                idx === 0 ? "bg-gradient-to-r from-yellow-100 to-yellow-50 border-2 border-yellow-300" :
                idx === 1 ? "bg-gray-100" :
                idx === 2 ? "bg-amber-50" :
                "bg-gray-50"
              }`}
            >
              <div className="flex items-center gap-4">
                <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                  idx === 0 ? "bg-yellow-500 text-white" :
                  idx === 1 ? "bg-gray-400 text-white" :
                  idx === 2 ? "bg-amber-600 text-white" :
                  "bg-gray-300 text-gray-700"
                }`}>
                  {entry.rank}
                </span>
                <span className="font-bold text-gray-900">{entry.nickname}</span>
                {entry.disc_type && (
                  <span className="text-sm text-gray-500">
                    {entry.disc_type === 'eagle' && '🦅'}
                    {entry.disc_type === 'parrot' && '🦜'}
                    {entry.disc_type === 'dove' && '🕊️'}
                    {entry.disc_type === 'owl' && '🦉'}
                  </span>
                )}
              </div>
              <span className="font-bold text-xl text-yellow-600">{entry.points} pts</span>
            </div>
          ))}
        </div>
      </div>

      {/* AI Business Plan Preview */}
      {businessPlan && (
        <div className="bg-white rounded-2xl p-8 shadow-lg mb-8">
          <h3 className="font-bold text-xl text-gray-900 mb-4 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            AI-Generated Business Plan
          </h3>
          <div className="prose prose-sm max-w-none">
            <div 
              className="whitespace-pre-wrap text-gray-700"
              dangerouslySetInnerHTML={{ 
                __html: businessPlan.business_plan
                  .replace(/## /g, '<h3 class="text-lg font-bold mt-4 mb-2 text-gray-900">')
                  .replace(/\n\n/g, '</h3><p>')
                  .replace(/\n- /g, '</p><li class="ml-4">')
              }} 
            />
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 justify-center">
        <button
          onClick={generateCertificate}
          disabled={loading}
          className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
        >
          <Award className="w-5 h-5" />
          Generate Certificate
        </button>
        <button
          onClick={generateBusinessPlan}
          disabled={generatingPlan || businessPlan}
          className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
        >
          {generatingPlan ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Sparkles className="w-5 h-5" />
          )}
          {businessPlan ? 'Plan Generated' : 'Generate Business Plan'}
        </button>
        <button
          onClick={resetDemo}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-full font-semibold transition-all"
        >
          Run Another Demo
        </button>
      </div>

      {/* Certificate Modal */}
      {showCertificate && certificate && (
        <GameCertificate 
          certificate={certificate}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-yellow-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {demoState === "setup" && renderSetup()}
        {demoState === "playing" && renderPlaying()}
        {demoState === "results" && renderResults()}
      </main>
    </div>
  );
}

export default GameDemoPage;
